import { Editor } from "react-draft-wysiwyg";
import { EditorState, ContentState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { useState, useEffect, useRef } from "react";
import htmlToDraft from 'html-to-draftjs';

/** Turn stored HTML into editor state; anything unreadable opens empty. */
const stateFromHtml = (html) => {
  if (typeof html !== 'string' || html.trim() === '') {
    return EditorState.createEmpty();
  }
  try {
    const { contentBlocks, entityMap } = htmlToDraft(html);
    if (! contentBlocks || contentBlocks.length === 0) {
      return EditorState.createEmpty();
    }
    return EditorState.createWithContent(
      ContentState.createFromBlockArray(contentBlocks, entityMap)
    );
  } catch (e) {
    return EditorState.createEmpty();
  }
};

/**
 * Rich text field.
 *
 * Three things used to go wrong here, all of them on the treatment
 * plan's notes:
 *
 *  - `onChange` was *called during render* (`onChange={props.onChange(data)}`
 *    rather than passed), so the parent's state was written from inside a
 *    child's render — React says as much in the console — and the very
 *    first render handed the parent `undefined`, wiping a note that had
 *    just been loaded from the server before it could be edited.
 *  - the HTML handed back was built from the previous editor state, so
 *    what was saved was always one keystroke behind.
 *  - the content was read from `inialData` once on mount, so a screen
 *    that fetches its note after mounting opened an empty editor over a
 *    plan that had notes.
 */
const BaseEditor = ({ inialData, onChange, ...rest }) => {
  const [editorState, setEditorState] = useState(() => stateFromHtml(inialData));

  // Only content the user has not touched is replaced when the prop
  // lands — retyping over someone's edit because a refetch came back
  // would be worse than opening slightly late.
  const touched = useRef(false);
  const lastLoaded = useRef(inialData);

  useEffect(() => {
    if (touched.current || inialData === lastLoaded.current) return;
    lastLoaded.current = inialData;
    setEditorState(stateFromHtml(inialData));
  }, [inialData]);

  const handleEditorStateChange = (state) => {
    touched.current = true;
    setEditorState(state);
    if (onChange) {
      onChange(convertToHTML(state.getCurrentContent()));
    }
  };

  return (
    <Editor
      { ...rest }
      editorState={ editorState }
      onEditorStateChange={ handleEditorStateChange }
    />
  );
};

export default BaseEditor;
